import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Briefcase, GraduationCap, Search, Filter, 
  Building, Clock, MapPin, Tags
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import CareerGuideDialog from "@/components/career/CareerGuideDialog";

interface Career {
  id: string;
  title: string;
  description: string;
  skills: string[];
  education: string;
  salary: string;
  growth: string;
  image: string;
  category: string;
}

const Explore = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [selectedCareer, setSelectedCareer] = useState<Career | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [careers, setCareers] = useState<Career[]>([]);

  // Redirect unauthenticated users to login
  useEffect(() => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to explore career paths.",
        variant: "default"
      });
      navigate("/auth");
    }
  }, [user, navigate, toast]);

  // Load careers data (this would ideally come from an API)
  useEffect(() => {
    // Sample careers data - in a real app, this would be fetched from an API
    const careerData: Career[] = [
      {
        id: "software-engineer",
        title: "Software Engineer",
        description: "Design, develop, and maintain software systems and applications using programming languages, frameworks, and software development methodologies.",
        skills: ["Programming", "Problem-solving", "Algorithm design", "Testing"],
        education: "Bachelor's degree in Computer Science or related field",
        salary: "$80,000 - $150,000",
        growth: "Faster than average (22% by 2030)",
        image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80",
        category: "Technology"
      },
      {
        id: "data-scientist",
        title: "Data Scientist",
        description: "Analyze and interpret complex data to help organizations make better decisions. Combine statistics, mathematics, and programming skills.",
        skills: ["Statistics", "Machine Learning", "Python", "Data Visualization"],
        education: "Master's or PhD in Statistics, Computer Science, or related field",
        salary: "$95,000 - $165,000",
        growth: "Much faster than average (31% by 2030)",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
        category: "Technology"
      },
      {
        id: "ux-designer",
        title: "UX/UI Designer",
        description: "Create intuitive, accessible, and enjoyable user experiences for digital products. Focus on user research, wireframing, and prototyping.",
        skills: ["User Research", "Wireframing", "Prototyping", "Visual Design"],
        education: "Bachelor's degree in Design, HCI, or related field",
        salary: "$75,000 - $130,000",
        growth: "Average (8% by 2030)",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
        category: "Creative"
      },
      {
        id: "environmental-scientist",
        title: "Environmental Scientist",
        description: "Study the environment and address environmental problems like pollution and climate change. Develop solutions for environmental protection.",
        skills: ["Research", "Data Analysis", "Field Work", "Communication"],
        education: "Bachelor's degree in Environmental Science or related field",
        salary: "$65,000 - $120,000",
        growth: "Faster than average (8% by 2030)",
        image: "https://images.unsplash.com/photo-1500673922987-e212871fec22?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80",
        category: "Science"
      },
      {
        id: "marketing-specialist",
        title: "Digital Marketing Specialist",
        description: "Plan and execute digital marketing strategies across various platforms to increase brand awareness and drive conversions.",
        skills: ["Social Media", "SEO/SEM", "Content Creation", "Analytics"],
        education: "Bachelor's degree in Marketing, Communications, or related field",
        salary: "$60,000 - $110,000",
        growth: "Faster than average (10% by 2030)",
        image: "https://images.unsplash.com/photo-1557838923-2985c318be48?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1331&q=80",
        category: "Creative"
      },
      {
        id: "healthcare-administrator",
        title: "Healthcare Administrator",
        description: "Manage healthcare facilities, departments, or medical practices. Ensure delivery of efficient and quality healthcare services.",
        skills: ["Leadership", "Finance", "Healthcare Regulations", "Operations"],
        education: "Bachelor's or Master's degree in Healthcare Administration",
        salary: "$70,000 - $120,000",
        growth: "Much faster than average (32% by 2030)",
        image: "https://images.unsplash.com/photo-1504439468489-c8920d796a29?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1171&q=80",
        category: "Healthcare"
      }
    ];
    
    setCareers(careerData);
  }, []);

  const filters = [
    { name: "Technology", icon: <Briefcase className="h-4 w-4 mr-2" /> },
    { name: "Healthcare", icon: <Building className="h-4 w-4 mr-2" /> },
    { name: "Creative", icon: <Tags className="h-4 w-4 mr-2" /> },
    { name: "Science", icon: <MapPin className="h-4 w-4 mr-2" /> }
  ];

  const handleExploreCareer = (career: Career) => {
    setSelectedCareer(career);
    setDialogOpen(true);
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(activeFilter === filter ? null : filter);
  };

  // Filter careers based on search term and active category filter
  const filteredCareers = careers.filter(career => {
    const matchesSearch = career.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      career.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !activeFilter || career.category === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  // If not authenticated, don't render the actual page content
  if (!user) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <main className="flex-grow">
        {/* Hero section */}
        <section className="bg-muted/30 py-12 px-4">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Explore Career Paths</h1>
              <p className="text-muted-foreground max-w-3xl mx-auto">
                Discover cool careers that match your interests and learn what skills you'll need to succeed.
              </p>
            </div>
            
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search for careers..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {filters.map((filter) => (
                <Button
                  key={filter.name}
                  size="sm"
                  variant={activeFilter === filter.name ? "default" : "outline"}
                  onClick={() => handleFilterClick(filter.name)}
                  className="flex items-center"
                >
                  {filter.icon}
                  {filter.name}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Career paths section */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-7xl">
            <Tabs defaultValue="careers" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
                <TabsTrigger value="careers" className="flex items-center justify-center">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Career Paths
                </TabsTrigger>
                <TabsTrigger value="education" className="flex items-center justify-center">
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Education
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="careers" className="animate-fade-in">
                {filteredCareers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCareers.map((career) => (
                      <Card key={career.id} className="overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                        <div className="h-48 relative overflow-hidden">
                          <img 
                            src={career.image} 
                            alt={career.title} 
                            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          />
                          <div className="absolute top-2 right-2">
                            <Badge>{career.category}</Badge>
                          </div>
                        </div>
                        <CardContent className="p-5">
                          <h3 className="font-semibold text-lg">{career.title}</h3>
                          <p className="text-muted-foreground text-sm mt-2 line-clamp-3">
                            {career.description}
                          </p>
                          
                          <div className="mt-4 flex flex-wrap gap-2">
                            {career.skills.slice(0, 2).map((skill) => (
                              <Badge key={skill} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {career.skills.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{career.skills.length - 2} more
                              </Badge>
                            )}
                          </div>
                          
                          <div className="mt-4 flex items-center text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>Growth: {career.growth}</span>
                          </div>
                          
                          <Button 
                            className="w-full mt-4"
                            onClick={() => handleExploreCareer(career)}
                          >
                            Explore This Career
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">
                      {activeFilter 
                        ? `No careers found in the "${activeFilter}" category matching "${searchTerm}"`
                        : `No careers found matching "${searchTerm}"`}
                    </p>
                    <div className="flex gap-2 justify-center mt-4">
                      {activeFilter && (
                        <Button 
                          variant="outline" 
                          onClick={() => setActiveFilter(null)}
                        >
                          Clear filter
                        </Button>
                      )}
                      {searchTerm && (
                        <Button 
                          variant="outline" 
                          onClick={() => setSearchTerm("")}
                        >
                          Clear search
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="education" className="animate-fade-in">
                <div className="text-center py-16 max-w-2xl mx-auto">
                  <GraduationCap className="h-12 w-12 mx-auto text-primary/70" />
                  <h3 className="text-xl font-medium mt-4">Education pathways coming soon!</h3>
                  <p className="text-muted-foreground mt-2">
                    We're working on bringing you cool information about different ways to learn,
                    including colleges, training programs, online courses, and more.
                  </p>
                  <Button variant="outline" className="mt-6">
                    Get notified when this launches
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
        
        {/* Mentorship CTA */}
        <section className="bg-primary/10 py-12 px-4">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-2xl font-bold mb-4">Get personalized career guidance</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
              Connect with experienced professionals who can answer your questions
              and help you explore different career options.
            </p>
            <Button asChild>
              <a href="/mentors">Find a Mentor</a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* Career Guide Dialog */}
      {selectedCareer && (
        <CareerGuideDialog 
          career={selectedCareer}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </div>
  );
};

export default Explore;
